import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const WishlistContext = createContext();

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlistIds, setWishlistIds] = useState(new Set());
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchWishlist = useCallback(async () => {
    if (!user) { setWishlistIds(new Set()); setWishlistItems([]); return; }
    setLoading(true);
    try {
      const { data } = await api.get('/wishlist');
      const items = data.items || [];
      setWishlistItems(items);
      setWishlistIds(new Set(items.map(i => i.product_id)));
    } catch {} finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchWishlist(); }, [fetchWishlist]);

  const toggleWishlist = async (product_id) => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: window.location.pathname }, wishlistProductId: product_id } });
      return false;
    }
    const isWishlisted = wishlistIds.has(product_id);
    // Optimistic update
    setWishlistIds(prev => {
      const next = new Set(prev);
      isWishlisted ? next.delete(product_id) : next.add(product_id);
      return next;
    });
    try {
      await api.post(`/wishlist/${product_id}`);
      toast.success(isWishlisted ? 'Removed from wishlist' : 'Added to wishlist!', { icon: isWishlisted ? '💔' : '❤️', duration: 1500 });
      if (isWishlisted) {
        setWishlistItems(prev => prev.filter(i => i.product_id !== product_id));
      } else {
        // Fetch full wishlist to get product details for the new item
        const { data } = await api.get('/wishlist');
        const items = data.items || [];
        setWishlistItems(items);
        setWishlistIds(new Set(items.map(i => i.product_id)));
      }
      return !isWishlisted;
    } catch {
      // Revert on error
      setWishlistIds(prev => {
        const next = new Set(prev);
        isWishlisted ? next.add(product_id) : next.delete(product_id);
        return next;
      });
      toast.error('Failed to update wishlist');
      return isWishlisted;
    }
  };

  const isWishlisted = (product_id) => wishlistIds.has(product_id);

  return (
    <WishlistContext.Provider value={{ wishlistIds, wishlistItems, loading, toggleWishlist, isWishlisted, fetchWishlist }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
